<script lang="ts" setup>
import { ElButton, ElDialog, ElForm, ElFormItem } from 'element-plus'
import { startCase } from 'lodash'
import { useSwapEmptyDialogLogic } from '~/compositions/useSwapEmptyDialogLogic'
import { getContainerTypeDescription, getTerminalLabel } from '~/constants'
import type { Appointment } from '~/models/appointments'
import type { ContainerType, ShippingLine } from '~/services/apiClient'
import { ContainerWatchState } from '~/services/apiClient'
import EmptyContainerSelector from '~/components/inputs/EmptyContainerSelector.vue'
import ReturnRuleInfoModal from '~/components/emptyReturnRules/ReturnRuleInfoModal.vue'
import ShippingLineSelector from '~/components/inputs/ShippingLineSelector.vue'
import ContainerTypeSelector from '~/components/inputs/ContainerTypeSelector.vue'
import ChassisTypeSelector from '~/components/inputs/ChassisTypeSelector.vue'
import Help from '~/components/display/Help.vue'

const props = withDefaults(
  defineProps<{
    appointment: Appointment
    showReturnRules: boolean
    // If line+type are passed, we "fix" the dialog to only work with these values
    shippingLine?: ShippingLine
    containerType?: ContainerType
    prefillContainerNumber?: string | null
    appendToBody?: boolean
  }>(),
  { appendToBody: true }
)

const emit = defineEmits<{ (e: 'close'): void }>()

// Use the composable
const {
  selectedContainer,
  EMPTY_CONTAINER_FIELD_NAME,
  formRef,
  isOwnChassis,
  loadingEmptyData,
  form,
  formRules,
  terminalLabel,
  receivingOptionsToShowContainersFor,
  receivingShippingLines,
  selectedShippingLine,
  receivingRule,
  shouldShowDualAlert,
  shouldShowChassisFields,
  shouldShowReceivingRules,
  shouldShowSwapButton,
  isSwapButtonDisabled,
  shouldShowBetaWarning,
  validateField,
  getShippingLineName,
  submitForm,
  initialize,
} = useSwapEmptyDialogLogic({
  appointment: props.appointment,
  showReturnRules: props.showReturnRules,
  shippingLine: props.shippingLine,
  containerType: props.containerType,
  prefillContainerNumber: props.prefillContainerNumber,
})

const dialogOpen = ref(true)
const receivingRulesDialogOpen = ref(false)

function closeDialog() {
  dialogOpen.value = false
  emit('close')
}

const title = computed(() => {
  return 'Swap Empty'
})

async function handleSubmitForm() {
  const success = await submitForm()
  if (success) {
    closeDialog()
  }
}

onMounted(() => {
  initialize()
})
</script>

<template>
  <ElDialog
    :model-value="dialogOpen"
    :append-to-body="props.appendToBody"
    :title="title"
    width="34%"
    center
    destroy-on-close
    @close="emit('close')"
  >
    <div v-if="shouldShowDualAlert" class="mb-2">
      <el-alert type="success" :closable="false">
        <i-mdi:information class="align-middle" />
        Empty in appointment not required for duals at
        {{ terminalLabel }}
      </el-alert>
    </div>
    <ElForm
      v-else
      ref="formRef"
      :model="form"
      :rules="formRules"
      label-width="auto"
      status-icon
      size="small"
      class="swap-empty-dialog-form"
      @validate="validateField"
    >
      <ElFormItem label="Existing Container #:">
        {{ props.appointment.container_number }}
      </ElFormItem>
      <ElFormItem label="Terminal:">
        {{ terminalLabel }}
      </ElFormItem>
      <ElFormItem label="Shift:">
        {{ props.appointment.scheduledShift }}
      </ElFormItem>
      <ElFormItem
        v-if="props.showReturnRules"
        label="Receiving singles/duals for:"
      >
        <div>
          <ColoredBadge
            v-for="shippingLineInfo in receivingShippingLines"
            :key="shippingLineInfo.shipping_line"
            effect="dark"
            size="default"
            class="mr-2"
          >
            {{ shippingLineInfo.long_desc }}
          </ColoredBadge>
        </div>
      </ElFormItem>
      <ElFormItem label="Empty container #:" :prop="EMPTY_CONTAINER_FIELD_NAME">
        <EmptyContainerSelector
          v-model="form.empty_container_number"
          :limit-to-line-and-types="receivingOptionsToShowContainersFor"
          class="empty-selector"
        />
        <Help title="Selecting an empty" class="ml-1">
          <p>
            Select from a list of recently out-gated import containers without a
            return appointment and where the container type and shipping line
            are being received for this terminal shift.
          </p>
          <p>You are can also enter any container number you like manually.</p>
          <p>You can also type to filter in several ways:</p>
          <ul>
            <li>Customer name. Example: Walmart</li>
            <li>Shipping line. Example: ONE</li>
            <li>Container type. Example: 40SD</li>
            <li>Line and type. Example: ONE/40SD</li>
            <li>Container number prefix. Example: SEGU</li>
          </ul>
        </Help>
        <template v-if="selectedContainer">
          <div v-if="selectedContainer.import_out_gated_time">
            Container was out-gated
            {{ selectedContainer.import_out_gated_time.toRelative() }}
            <template v-if="selectedContainer.last_related_terminal">
              from
              {{ getTerminalLabel(selectedContainer.last_related_terminal) }}
            </template>
          </div>
          <div
            v-if="
              selectedContainer.cycle_state !==
              ContainerWatchState.ImportPickedUp
            "
            class="text-red-600"
          >
            Container not an out-gated import. Status:
            <i> {{ startCase(selectedContainer.cycle_state) }}</i>
          </div>
        </template>
        <div
          v-if="
            !selectedContainer &&
            form.empty_container_number &&
            !loadingEmptyData
          "
          class="text-orange-400"
        >
          {{ form.empty_container_number }} not found in Dray Dog.
          <span class="text-black"
            >This is fine, just double check you have a valid empty</span
          >
        </div>
      </ElFormItem>
      <!-- TODO: make these dropdowns -->
      <ElFormItem label="Shipping line:" prop="shipping_line">
        <span v-if="props.shippingLine">
          {{ getShippingLineName(props.shippingLine) }}
        </span>
        <ShippingLineSelector
          v-else
          v-model="form.shipping_line"
          v-loading="loadingEmptyData"
          class="line-type-selectors"
        />
      </ElFormItem>
      <ElFormItem label="Container type:" prop="container_type">
        <span v-if="props.containerType">
          {{ getContainerTypeDescription(props.containerType) }}
        </span>
        <ContainerTypeSelector
          v-else
          v-model="form.container_type"
          v-loading="loadingEmptyData"
          class="line-type-selectors"
        />
      </ElFormItem>
      <ElFormItem v-if="shouldShowChassisFields" label="Chassis Type:">
        <ChassisTypeSelector v-model="isOwnChassis" />
      </ElFormItem>
      <ElFormItem
        v-if="shouldShowChassisFields"
        label="Chassis Number:"
        prop="chassis_number"
      >
        <el-input
          v-if="isOwnChassis"
          v-model="form.chassis_number"
          size="large"
          placeholder="FLTZ406909"
          class="max-w-196px"
        />
        <el-input
          v-else
          size="large"
          placeholder="Not Required"
          class="max-w-196px"
          disabled
        />
      </ElFormItem>
      <template v-if="shouldShowReceivingRules">
        <el-alert
          v-if="receivingRule"
          :type="receivingRule.can_dual ? 'success' : 'error'"
          effect="dark"
          show-icon
          :closable="false"
        >
          <template #title>
            <div class="flex justify-between">
              <div class="mr-2">
                {{ terminalLabel }} is
                {{ receivingRule.can_dual ? 'accepting' : 'not accepting' }}
                duals for
                {{ selectedShippingLine?.long_desc }}
                {{ form.container_type }} empties for
                {{ props.appointment.scheduledShift }}
              </div>
              <ElButton size="default" @click="receivingRulesDialogOpen = true">
                View Rules
              </ElButton>
            </div>
          </template>
        </el-alert>
        <el-alert
          v-else-if="props.showReturnRules"
          type="warning"
          effect="dark"
          show-icon
          :closable="false"
        >
          <template #title>
            We don't know what
            {{ terminalLabel }}'s return rules are for
            {{ selectedShippingLine?.long_desc }}
            {{ form.container_type }}
            {{ props.appointment.scheduledShift }}
          </template>
          <template #default>
            It's likely that the terminal is not accepting at this time, but you
            should confirm yourself
          </template>
        </el-alert>
      </template>

      <el-alert
        v-if="shouldShowBetaWarning"
        type="warning"
        show-icon
        :closable="false"
        class="mb-2"
      >
        Swap Empty is a beta product. It may not work every time.
      </el-alert>
    </ElForm>
    <template #footer>
      <div class="flex">
        <ElButton
          size="large"
          class="flex-1 fs-close-swap-empty-dialog"
          @click="closeDialog"
        >
          Cancel
        </ElButton>
        <ElButton
          v-if="shouldShowSwapButton"
          size="large"
          type="primary"
          class="flex-1 fs-swap-empty"
          :disabled="isSwapButtonDisabled"
          @click="handleSubmitForm"
        >
          <div>Swap Empty</div>
        </ElButton>
      </div>
    </template>
  </ElDialog>

  <ReturnRuleInfoModal
    v-if="receivingRulesDialogOpen && receivingRule && props.showReturnRules"
    :model-value="receivingRulesDialogOpen"
    :return-rule="receivingRule"
    :append-to-body="props.appendToBody"
    @close="receivingRulesDialogOpen = false"
  />
</template>

<style lang="scss">
.swap-empty-dialog-form .el-form-item__label-wrap {
  @apply items-center;
}
.swap-empty-dialog-form .el-form-item__label {
  font-weight: bold;
}
.swap-empty-dialog-form .el-form-item {
  margin-right: 15px;
}

.empty-selector {
  max-width: 80%;
}

.line-type-selectors {
  max-width: 80%;
}
</style>
